import React, { createContext, useContext, useRef, useState } from "react";
import {
  RemissionNote,
  RemissionNoteItem,
  newRemissionNote,
  newRemissionNoteItem,
} from "../../../models/remission-note";
import Establishment from "../../../models/establishment";
import DispatchPoint from "../../../models/dispatch-point";
import Stamp from "../../../models/stamp";
import Client from "../../../models/client";
import { Invoice } from "../../../models/invoice";
import Department from "../../../models/department";
import District from "../../../models/district";
import City from "../../../models/city";
import Errors from "../../../models/errors";
import moment from "moment";
import { changeTZToUtc } from "../../../utils/dates";
import {
  ASSOCIATED_DOCUMENT_TYPE_ELECTRONIC_INVOICE,
  ASSOCIATED_DOCUMENT_TYPE_NONE,
  ASSOCIATED_DOCUMENT_TYPE_PRINTED_INVOICE,
} from "../../../models/associated-document";

interface RemissionNoteFormContextValue {
  // Refs and State
  remissionNoteRef: React.MutableRefObject<RemissionNote | null>;
  remissionNoteDate: Date | undefined;
  establishment: Establishment | null;
  dispatchPoint: DispatchPoint | null;
  stamp: Stamp | null;
  client: Client | null;
  associatedDocumentType: string | undefined;
  electronicInvoice: Invoice | null;
  transportDepartureDepartment: Department | null;
  transportDepartureDistrict: District | null;
  transportDepartureCity: City | null;
  transportArrivalDepartment: Department | null;
  transportArrivalDistrict: District | null;
  transportArrivalCity: City | null;
  items: RemissionNoteItem[];
  errors: Errors;
  readonly?: boolean;

  // Setters
  setRemissionNoteDate: (date: Date | undefined) => void;
  setEstablishment: (est: Establishment | null) => void;
  setDispatchPoint: (dp: DispatchPoint | null) => void;
  setStamp: (stamp: Stamp | null) => void;
  setClient: (client: Client | null) => void;
  setAssociatedDocumentType: (type: string | undefined) => void;
  setElectronicInvoice: (invoice: Invoice | null) => void;
  setTransportDepartureDepartment: (dept: Department | null) => void;
  setTransportDepartureDistrict: (dist: District | null) => void;
  setTransportDepartureCity: (city: City | null) => void;
  setTransportArrivalDepartment: (dept: Department | null) => void;
  setTransportArrivalDistrict: (dist: District | null) => void;
  setTransportArrivalCity: (city: City | null) => void;
  setItems: (items: RemissionNoteItem[]) => void;
  setErrors: (errors: Errors) => void;

  // Handlers
  onEstablishmentChange: (newEstablishment: Establishment | null) => void;
  onDispatchPointChange: (newDispatchPoint: DispatchPoint | null) => void;
  onStampChange: (newStamp: Stamp | null) => void;
  onClientChange: (newClient: Client | null) => void;
  onRemissionNoteDateChange: (value: string | moment.Moment) => void;
  onDncpContractCodeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDncpContractModalityChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onDncpContractEntityChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDncpContractYearChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDncpContractSequenceChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onDncpContractDateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onReasonChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onResponsibleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onEstimatedKilometersChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDescriptionChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onObsChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAssociatedDocumentTypeChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onElectronicInvoiceChange: (invoice: Invoice | null) => void;
  onAssociatedDocumentStampIdentifierChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onAssociatedDocumentEstablishmentCodeChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onAssociatedDocumentDispatchPointCodeChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onAssociatedDocumentNumberChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onAssociatedDocumentDateChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onTransportModeCodeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onTransportEstimatedStartDate: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onTransportEstimatedEndDate: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onTransportTypeCodeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onTransportResponsibleCodeChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onTransportDepartureAddressChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onTransportDepartureHouseNumberChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onTransportDepartureDepartmentChange: (dept: Department | null) => void;
  onTransportDepartureDistrictChange: (dist: District | null) => void;
  onTransportDepartureCityChange: (city: City | null) => void;
  onTransportArrivalAddressChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onTransportArrivalHouseNumberChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onTransportArrivalDepartmentChange: (dept: Department | null) => void;
  onTransportArrivalDistrictChange: (dist: District | null) => void;
  onTransportArrivalCityChange: (city: City | null) => void;
  ontransportVehicleTypeChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onTransportVehicleBrandChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onTransportVehicleDocumentTypeCodeChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onTransportVehicleDocumentNumberChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onTransportVehicleRegistrationNumberChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onCarrierIsTaxPayerChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCarrierRucChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCarrierSocialNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCarrierAddressChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDriverDocumentNumberChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onDriverNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDriverAddressChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onItemChange: (index: number, item: RemissionNoteItem) => void;
  onItemDelete: (index: number) => void;
  onItemsChange: (newItems: RemissionNoteItem[]) => void;
  onAddItemClick: () => void;
}

const RemissionNoteFormContext = createContext<
  RemissionNoteFormContextValue | undefined
>(undefined);

export const useRemissionNoteFormContext = () => {
  const context = useContext(RemissionNoteFormContext);
  if (!context) {
    throw new Error(
      "useRemissionNoteFormContext must be used within RemissionNoteFormProvider"
    );
  }
  return context;
};

interface RemissionNoteFormProviderProps {
  children: React.ReactNode;
  taxPayerId?: number;
  readonly?: boolean;
}

export const RemissionNoteFormProvider: React.FC<
  RemissionNoteFormProviderProps
> = ({ children, taxPayerId, readonly }) => {
  const remissionNoteRef = useRef<RemissionNote | null>(
    newRemissionNote(taxPayerId)
  );
  const [remissionNoteDate, setRemissionNoteDate] = useState<Date | undefined>(
    undefined
  );
  const [establishment, setEstablishment] = useState<Establishment | null>(
    null
  );
  const [dispatchPoint, setDispatchPoint] = useState<DispatchPoint | null>(
    null
  );
  const [stamp, setStamp] = useState<Stamp | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [associatedDocumentType, setAssociatedDocumentType] = useState<
    string | undefined
  >(ASSOCIATED_DOCUMENT_TYPE_NONE);
  const [electronicInvoice, setElectronicInvoice] = useState<Invoice | null>(
    null
  );
  const [transportDepartureDepartment, setTransportDepartureDepartment] =
    useState<Department | null>(null);
  const [transportDepartureDistrict, setTransportDepartureDistrict] =
    useState<District | null>(null);
  const [transportDepartureCity, setTransportDepartureCity] =
    useState<City | null>(null);
  const [transportArrivalDepartment, setTransportArrivalDepartment] =
    useState<Department | null>(null);
  const [transportArrivalDistrict, setTransportArrivalDistrict] =
    useState<District | null>(null);
  const [transportArrivalCity, setTransportArrivalCity] = useState<City | null>(
    null
  );
  const [items, setItems] = useState<RemissionNoteItem[]>([]);
  const [errors, setErrors] = useState<Errors>({});

  const onEstablishmentChange = (newEstablishment: Establishment | null) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.establishmentId = newEstablishment?.id;
    remissionNoteRef.current.establishmentCode = newEstablishment?.code;
    setEstablishment(newEstablishment);
  };

  const onDispatchPointChange = (newDispatchPoint: DispatchPoint | null) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.dispatchPointId = newDispatchPoint?.id;
    remissionNoteRef.current.dispatchPointIdentifier =
      newDispatchPoint?.identifier;
    setDispatchPoint(newDispatchPoint);
  };

  const onStampChange = (newStamp: Stamp | null) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.stampId = newStamp?.id;
    remissionNoteRef.current.stampIdentifier = newStamp?.identifier;
    remissionNoteRef.current.stampBeginDate = newStamp?.beginDate;
    setStamp(newStamp);
  };

  const onClientChange = (newClient: Client | null) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.clientId = newClient?.id;
    remissionNoteRef.current.clientCode = newClient?.code;
    remissionNoteRef.current.clientIsTaxPayer = newClient?.isTaxPayer;
    remissionNoteRef.current.clientRuc = newClient?.ruc;
    remissionNoteRef.current.clientSocialName = newClient?.socialName;
    remissionNoteRef.current.clientFantasyName = newClient?.fantasyName;
    remissionNoteRef.current.clientType = newClient?.type;
    remissionNoteRef.current.clientCountryCode = newClient?.countryCode;
    remissionNoteRef.current.clientHasAddress = newClient?.hasAddress;
    remissionNoteRef.current.clientAddress = newClient?.address;
    remissionNoteRef.current.clientHouseNumber = newClient?.houseNumber;
    remissionNoteRef.current.clientDepartment = newClient?.department;
    remissionNoteRef.current.clientDepartmentDescription =
      newClient?.departmentDescription;
    remissionNoteRef.current.clientDistrict = newClient?.district;
    remissionNoteRef.current.clientDistrictDescription =
      newClient?.districtDescription;
    remissionNoteRef.current.clientCity = newClient?.city;
    remissionNoteRef.current.clientCityDescription = newClient?.cityDescription;
    remissionNoteRef.current.clientPhoneNumber = newClient?.phoneNumber;
    remissionNoteRef.current.clientEmail = newClient?.email;
    setClient(newClient);
  };

  const onRemissionNoteDateChange = (value: string | moment.Moment) => {
    if (remissionNoteRef.current === null) return;
    const newDate = value as moment.Moment;
    if (newDate === undefined || !moment(value, true).isValid()) return;
    const remissionNoteDate = new Date((value as moment.Moment).toISOString());
    setRemissionNoteDate(remissionNoteDate);
    remissionNoteRef.current.remissionNoteDate =
      changeTZToUtc(remissionNoteDate).toISOString();
  };

  const onDncpContractCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.dncpContractCode = e.target.value;
  };

  const onDncpContractModalityChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.dncpContractModality = e.target.value;
  };

  const onDncpContractEntityChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.dncpContractEntity = e.target.value;
  };

  const onDncpContractYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.dncpContractYear = e.target.value;
  };

  const onDncpContractSequenceChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.dncpContractSequence = e.target.value;
  };

  const onDncpContractDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.dncpContractDate = e.target.value;
  };

  const onReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (remissionNoteRef.current === null) return;
    if (!isNaN(parseInt(e.target.value))) {
      remissionNoteRef.current.reasonCode = parseInt(e.target.value);
    } else {
      remissionNoteRef.current.reasonCode = undefined;
    }
  };

  const onResponsibleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (remissionNoteRef.current === null) return;
    if (!isNaN(parseInt(e.target.value))) {
      remissionNoteRef.current.responsibleCode = parseInt(e.target.value);
    } else {
      remissionNoteRef.current.responsibleCode = undefined;
    }
  };

  const onEstimatedKilometersChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (remissionNoteRef.current === null) return;
    if (!isNaN(parseInt(e.target.value))) {
      remissionNoteRef.current.estimatedKilometers = parseInt(e.target.value);
    } else {
      remissionNoteRef.current.estimatedKilometers = undefined;
    }
  };

  const onDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.description = e.target.value;
  };

  const onObsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.obs = e.target.value;
  };

  const onAssociatedDocumentTypeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (remissionNoteRef.current === null) return;
    const newType = e.target.value || ASSOCIATED_DOCUMENT_TYPE_NONE;
    remissionNoteRef.current.associatedDocumentType = newType;
    remissionNoteRef.current.hasAssociatedDocument =
      newType !== ASSOCIATED_DOCUMENT_TYPE_NONE;

    if (newType !== ASSOCIATED_DOCUMENT_TYPE_PRINTED_INVOICE) {
      remissionNoteRef.current.associatedDocumentPrintedType = undefined;
      remissionNoteRef.current.associatedDocumentStampIdentifier = undefined;
      remissionNoteRef.current.associatedDocumentEstablishmentCode = undefined;
      remissionNoteRef.current.associatedDocumentDispatchPointCode = undefined;
      remissionNoteRef.current.associatedDocumentNumber = undefined;
      remissionNoteRef.current.associatedDocumentDate = undefined;
    }
    if (newType !== ASSOCIATED_DOCUMENT_TYPE_ELECTRONIC_INVOICE) {
      remissionNoteRef.current.associatedDocumentCdc = undefined;
      remissionNoteRef.current.invoiceId = undefined;
      setElectronicInvoice(null);
    }
    if (newType === ASSOCIATED_DOCUMENT_TYPE_PRINTED_INVOICE) {
      remissionNoteRef.current.associatedDocumentPrintedType =
        ASSOCIATED_DOCUMENT_TYPE_PRINTED_INVOICE;
    }

    setAssociatedDocumentType(newType);
  };

  const onElectronicInvoiceChange = (invoice: Invoice | null) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.invoiceId = invoice?.id;
    remissionNoteRef.current.associatedDocumentCdc = invoice?.cdc;
    setElectronicInvoice(invoice);
  };

  const onAssociatedDocumentStampIdentifierChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.associatedDocumentStampIdentifier = e.target.value;
  };

  const onAssociatedDocumentEstablishmentCodeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.associatedDocumentEstablishmentCode =
      e.target.value;
  };

  const onAssociatedDocumentDispatchPointCodeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.associatedDocumentDispatchPointCode =
      e.target.value;
  };

  const onAssociatedDocumentNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.associatedDocumentNumber = e.target.value;
  };

  const onAssociatedDocumentDateChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.associatedDocumentDate = e.target.value;
  };

  const onTransportModeCodeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (remissionNoteRef.current === null) return;
    if (!isNaN(parseInt(e.target.value))) {
      remissionNoteRef.current.transportModeCode = parseInt(e.target.value);
    } else {
      remissionNoteRef.current.transportModeCode = undefined;
    }
  };

  const onTransportEstimatedStartDate = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.transportEstimatedStartDate = e.target.value;
  };

  const onTransportEstimatedEndDate = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.transportEstimatedEndDate = e.target.value;
  };

  const onTransportTypeCodeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (remissionNoteRef.current === null) return;
    if (!isNaN(parseInt(e.target.value))) {
      remissionNoteRef.current.transportTypeCode = parseInt(e.target.value);
    } else {
      remissionNoteRef.current.transportTypeCode = undefined;
    }
  };

  const onTransportResponsibleCodeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (remissionNoteRef.current === null) return;
    if (!isNaN(parseInt(e.target.value))) {
      remissionNoteRef.current.transportResponsibleCode = parseInt(
        e.target.value
      );
    } else {
      remissionNoteRef.current.transportResponsibleCode = undefined;
    }
  };

  const onTransportDepartureAddressChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.transportDepartureAddress = e.target.value;
  };

  const onTransportDepartureHouseNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (remissionNoteRef.current === null) return;
    if (!isNaN(parseInt(e.target.value))) {
      remissionNoteRef.current.transportDepartureHouseNumber = parseInt(
        e.target.value
      );
    } else {
      remissionNoteRef.current.transportDepartureHouseNumber = undefined;
    }
  };

  const onTransportDepartureDepartmentChange = (
    newDepartment: Department | null
  ) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.transportDepartureDepartment = newDepartment?.code;
    remissionNoteRef.current.transportDepartureDepartmentDescription =
      newDepartment?.description;
    setTransportDepartureDepartment(newDepartment);
    if (newDepartment === null) {
      setTransportDepartureDistrict(null);
      setTransportDepartureCity(null);
      remissionNoteRef.current.transportDepartureDistrict = undefined;
      remissionNoteRef.current.transportDepartureDistrictDescription =
        undefined;
      remissionNoteRef.current.transportDepartureCity = undefined;
      remissionNoteRef.current.transportDepartureCityDescription = undefined;
    }
  };

  const onTransportDepartureDistrictChange = (newDistrict: District | null) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.transportDepartureDistrict = newDistrict?.code;
    remissionNoteRef.current.transportDepartureDistrictDescription =
      newDistrict?.description;
    setTransportDepartureDistrict(newDistrict);
    if (newDistrict === null) {
      setTransportDepartureCity(null);
      remissionNoteRef.current.transportDepartureCity = undefined;
      remissionNoteRef.current.transportDepartureCityDescription = undefined;
    }
  };

  const onTransportDepartureCityChange = (newCity: City | null) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.transportDepartureCity = newCity?.code;
    remissionNoteRef.current.transportDepartureCityDescription =
      newCity?.description;
    setTransportDepartureCity(newCity);
  };

  const onTransportArrivalAddressChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.transportArrivalAddress = e.target.value;
  };

  const onTransportArrivalHouseNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (remissionNoteRef.current === null) return;
    if (!isNaN(parseInt(e.target.value))) {
      remissionNoteRef.current.transportArrivalHouseNumber = parseInt(
        e.target.value
      );
    } else {
      remissionNoteRef.current.transportArrivalHouseNumber = undefined;
    }
  };

  const onTransportArrivalDepartmentChange = (
    newDepartment: Department | null
  ) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.transportArrivalDepartment = newDepartment?.code;
    remissionNoteRef.current.transportArrivalDepartmentDescription =
      newDepartment?.description;
    setTransportArrivalDepartment(newDepartment);
    if (newDepartment === null) {
      setTransportArrivalDistrict(null);
      setTransportArrivalCity(null);
      remissionNoteRef.current.transportArrivalDistrict = undefined;
      remissionNoteRef.current.transportArrivalDistrictDescription = undefined;
      remissionNoteRef.current.transportArrivalCity = undefined;
      remissionNoteRef.current.transportArrivalCityDescription = undefined;
    }
  };

  const onTransportArrivalDistrictChange = (newDistrict: District | null) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.transportArrivalDistrict = newDistrict?.code;
    remissionNoteRef.current.transportArrivalDistrictDescription =
      newDistrict?.description;
    setTransportArrivalDistrict(newDistrict);
    if (newDistrict === null) {
      setTransportArrivalCity(null);
      remissionNoteRef.current.transportArrivalCity = undefined;
      remissionNoteRef.current.transportArrivalCityDescription = undefined;
    }
  };

  const onTransportArrivalCityChange = (newCity: City | null) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.transportArrivalCity = newCity?.code;
    remissionNoteRef.current.transportArrivalCityDescription =
      newCity?.description;
    setTransportArrivalCity(newCity);
  };

  const ontransportVehicleTypeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.transportVehicleType = e.target.value;
  };

  const onTransportVehicleBrandChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.transportVehicleBrand = e.target.value;
  };

  const onTransportVehicleDocumentTypeCodeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (remissionNoteRef.current === null) return;
    if (!isNaN(parseInt(e.target.value))) {
      remissionNoteRef.current.transportVehicleDocumentTypeCode = parseInt(
        e.target.value
      );
    } else {
      remissionNoteRef.current.transportVehicleDocumentTypeCode = undefined;
    }
  };

  const onTransportVehicleDocumentNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.transportVehicleDocumentNumber = e.target.value;
  };

  const onTransportVehicleRegistrationNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.transportVehicleRegistrationNumber =
      e.target.value;
  };

  const onCarrierIsTaxPayerChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.carrierIsTaxPayer = e.target.checked;
  };

  const onCarrierRucChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.carrierRuc = e.target.value;
  };

  const onCarrierSocialNameChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.carrierSocialName = e.target.value;
  };

  const onCarrierAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.carrierAddress = e.target.value;
  };

  const onDriverDocumentNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.driverDocumentNumber = e.target.value;
  };

  const onDriverNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.driverName = e.target.value;
  };

  const onDriverAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current.driverAddress = e.target.value;
  };

  const onItemChange = (index: number, item: RemissionNoteItem) => {
    if (remissionNoteRef.current === null) return;
    const newItems = [...remissionNoteRef.current.items];
    newItems[index] = item;
    onItemsChange(newItems);
  };

  const onItemDelete = (index: number) => {
    if (remissionNoteRef.current === null) return;
    const newItems = [...remissionNoteRef.current.items];
    newItems.splice(index, 1);
    onItemsChange(newItems);
  };

  const onItemsChange = (newItems: RemissionNoteItem[]) => {
    if (remissionNoteRef.current === null) return;
    remissionNoteRef.current = {
      ...remissionNoteRef.current,
      items: newItems,
    };
    setItems(newItems);
  };

  const onAddItemClick = () => {
    if (remissionNoteRef.current === null) return;
    const newItems = [
      ...remissionNoteRef.current.items,
      newRemissionNoteItem(),
    ];
    onItemsChange(newItems);
  };

  const value: RemissionNoteFormContextValue = {
    remissionNoteRef,
    remissionNoteDate,
    establishment,
    dispatchPoint,
    stamp,
    client,
    associatedDocumentType,
    electronicInvoice,
    transportDepartureDepartment,
    transportDepartureDistrict,
    transportDepartureCity,
    transportArrivalDepartment,
    transportArrivalDistrict,
    transportArrivalCity,
    items,
    errors,
    readonly,
    setRemissionNoteDate,
    setEstablishment,
    setDispatchPoint,
    setStamp,
    setClient,
    setAssociatedDocumentType,
    setElectronicInvoice,
    setTransportDepartureDepartment,
    setTransportDepartureDistrict,
    setTransportDepartureCity,
    setTransportArrivalDepartment,
    setTransportArrivalDistrict,
    setTransportArrivalCity,
    setItems,
    setErrors,
    onEstablishmentChange,
    onDispatchPointChange,
    onStampChange,
    onClientChange,
    onRemissionNoteDateChange,
    onDncpContractCodeChange,
    onDncpContractModalityChange,
    onDncpContractEntityChange,
    onDncpContractYearChange,
    onDncpContractSequenceChange,
    onDncpContractDateChange,
    onReasonChange,
    onResponsibleChange,
    onEstimatedKilometersChange,
    onDescriptionChange,
    onObsChange,
    onAssociatedDocumentTypeChange,
    onElectronicInvoiceChange,
    onAssociatedDocumentStampIdentifierChange,
    onAssociatedDocumentEstablishmentCodeChange,
    onAssociatedDocumentDispatchPointCodeChange,
    onAssociatedDocumentNumberChange,
    onAssociatedDocumentDateChange,
    onTransportModeCodeChange,
    onTransportEstimatedStartDate,
    onTransportEstimatedEndDate,
    onTransportTypeCodeChange,
    onTransportResponsibleCodeChange,
    onTransportDepartureAddressChange,
    onTransportDepartureHouseNumberChange,
    onTransportDepartureDepartmentChange,
    onTransportDepartureDistrictChange,
    onTransportDepartureCityChange,
    onTransportArrivalAddressChange,
    onTransportArrivalHouseNumberChange,
    onTransportArrivalDepartmentChange,
    onTransportArrivalDistrictChange,
    onTransportArrivalCityChange,
    ontransportVehicleTypeChange,
    onTransportVehicleBrandChange,
    onTransportVehicleDocumentTypeCodeChange,
    onTransportVehicleDocumentNumberChange,
    onTransportVehicleRegistrationNumberChange,
    onCarrierIsTaxPayerChange,
    onCarrierRucChange,
    onCarrierSocialNameChange,
    onCarrierAddressChange,
    onDriverDocumentNumberChange,
    onDriverNameChange,
    onDriverAddressChange,
    onItemChange,
    onItemDelete,
    onItemsChange,
    onAddItemClick,
  };

  return (
    <RemissionNoteFormContext.Provider value={value}>
      {children}
    </RemissionNoteFormContext.Provider>
  );
};
